<template>
  <div class="control-slider container">
    <ul
      ref="slideWrapper"
      class="slide-wrapper"
      :class="[
        {
          'padding-container': !withoutContainer,
        },
        wrapperClasses,
      ]"
      :style="wrapperStyle"
    >
      <slot name="slides" />
    </ul>

    <div
      v-if="!hideNavigation"
      class="slide-controls"
      :class="[
        {
          'padding-container': !withoutContainer,
          'hide-desktop': hideNavigationDesktop,
        },
        controlClasses,
      ]"
      :style="controlStyle"
    >
      <div
        class="progress"
        :class="{
          'progress--light': variant === 'light',
        }"
      >
        <div
          class="progress__bar"
          :class="{
            'progress__bar--light': variant === 'light',
          }"
          :style="{ width: `${Math.max(2, scrollPercent)}%` }"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import debounce from '~/utils/helpers/debounce.js';

defineProps({
  variant: {
    type: String,
    default: 'dark',
    validator: (value) => ['light', 'dark'].includes(value),
  },
  cardWidth: {
    type: Number,
    default: 296,
  },
  cardsToMove: {
    type: Number,
    default: 1,
  },
  hideNavigation: {
    type: Boolean,
    default: false,
  },
  hideNavigationDesktop: {
    type: Boolean,
    default: false,
  },
  controlStyle: {
    type: String,
    default: '',
  },
  wrapperStyle: {
    type: String,
    default: '',
  },
  wrapperClasses: {
    type: String,
    default: '',
  },
  controlClasses: {
    type: String,
    default: '',
  },
  withoutContainer: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['scroll', 'scrollEnd']);
const slideWrapper = ref(null);
const scrollPercent = ref(0);

let resizeObserver = null;

const getScrollPercent = () => {
  const element = slideWrapper.value;
  if (!element) return;
  const percent =
    (100 * element.scrollLeft) / (element.scrollWidth - element.clientWidth);
  scrollPercent.value = percent.toFixed(2);
};

const onScrollEnd = () => {
  const element = slideWrapper.value;
  if (!element) return;
  emit('scrollEnd', {
    percent: scrollPercent.value,
    scrollLeft: element.scrollLeft,
    scrollWidth: element.scrollWidth,
    clientWidth: element.clientWidth,
  });
};

const debouncedGetScrollPercent = debounce(getScrollPercent, 200);
const debouncedOnScrollEnd = debounce(onScrollEnd, 300);

onMounted(() => {
  slideWrapper.value.addEventListener('scroll', getScrollPercent);
  slideWrapper.value.addEventListener('scroll', debouncedOnScrollEnd);

  resizeObserver = new ResizeObserver(() => debouncedGetScrollPercent());
  resizeObserver.observe(document.documentElement);
});

onBeforeUnmount(() => {
  slideWrapper.value?.removeEventListener('scroll', getScrollPercent);
  slideWrapper.value?.removeEventListener('scroll', debouncedOnScrollEnd);

  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});
</script>

<style lang="scss" src="./ControlSlider.scss"></style>
